import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ease } from "@/lib/motion";
import { ChevronRight, Check, ShieldCheck, AlertTriangle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export interface TimelinePhase {
  number: string;
  title: string;
  description: string;
  decision: string;
  output: string;
  riskRemoved?: string;
  whatChanges?: string;
}

interface SystemsTimelineProps {
  title?: string;
  label?: string;
  phases: TimelinePhase[];
}

const SystemsTimeline = ({
  title = "How we engineer the system.",
  label = "Process",
  phases,
}: SystemsTimelineProps) => {
  const isMobile = useIsMobile();
  const [activePhase, setActivePhase] = useState<number>(0); // Always have one active
  const [hoveredPhase, setHoveredPhase] = useState<number | null>(null);

  // On desktop, hover takes priority; on mobile/tablet, only active matters
  const currentPhase = isMobile ? activePhase : (hoveredPhase ?? activePhase);

  // Ensure first phase is active by default
  useEffect(() => {
    setActivePhase(0);
  }, []);

  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, ease }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
            {label}
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
            {title}
          </h2>
        </motion.div>

        {/* Desktop: Horizontal Timeline */}
        <div className="hidden lg:block">
          {/* Timeline Track */}
          <div className="relative">
            {/* Background track */}
            <div className="absolute top-6 left-0 right-0 h-px bg-border" />
            
            {/* Progress track */}
            <motion.div
              className="absolute top-6 left-0 h-px bg-primary"
              initial={{ width: "0%" }}
              whileInView={{ width: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
            />

            {/* Phase nodes */}
            <div className="relative flex justify-between">
              {phases.map((phase, index) => {
                const isActive = currentPhase === index;
                const isPast = currentPhase !== null && index < currentPhase;
                
                return (
                  <motion.div
                    key={phase.number}
                    className="relative flex flex-col items-center cursor-pointer group"
                    style={{ width: `${100 / phases.length}%` }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 * index, ease }}
                    onMouseEnter={() => setHoveredPhase(index)}
                    onMouseLeave={() => setHoveredPhase(null)}
                    onClick={() => setActivePhase(index)}
                  >
                    {/* Node */}
                    <motion.div
                      className={`
                        relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                        transition-all duration-300 border-2
                        ${isActive 
                          ? "bg-primary border-primary text-primary-foreground scale-110" 
                          : isPast
                            ? "bg-primary/10 border-primary/30 text-primary"
                            : "bg-background border-border text-muted-foreground group-hover:border-primary/50"
                        }
                      `}
                      whileHover={{ scale: 1.05 }}
                    >
                      {isPast ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-semibold">{phase.number}</span>
                      )}
                    </motion.div>

                    {/* Phase title */}
                    <div className="mt-4 text-center px-2">
                      <p className={`
                        text-sm font-medium transition-colors duration-300
                        ${isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"}
                      `}>
                        {phase.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {phase.description}
                      </p>
                    </div>

                    {/* Expand indicator */}
                    <motion.div
                      className="mt-3 text-muted-foreground"
                      animate={{ rotate: isActive ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Fixed Height Detail Panel Container - Never collapses */}
          <div className="mt-12 min-h-[180px]">
            <div className="bg-card border border-border rounded-xl p-8 h-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPhase}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="grid md:grid-cols-3 gap-8"
                >
                  {/* Decision Made */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Decision Made
                      </span>
                    </div>
                    <p className="text-foreground font-medium">
                      {phases[currentPhase].decision}
                    </p>
                  </div>

                  {/* Output Delivered */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                        <ChevronRight className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Output
                      </span>
                    </div>
                    <p className="text-foreground font-medium">
                      {phases[currentPhase].output}
                    </p>
                  </div>

                  {/* Risk Removed (if provided) */}
                  {phases[currentPhase].riskRemoved && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                          <ShieldCheck className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          Risk Removed
                        </span>
                      </div>
                      <p className="text-foreground font-medium">
                        {phases[currentPhase].riskRemoved}
                      </p>
                    </div>
                  )}

                  {/* What Changes (if provided and no risk) */}
                  {!phases[currentPhase].riskRemoved && phases[currentPhase].whatChanges && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        </div>
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          What Changes
                        </span>
                      </div>
                      <p className="text-foreground font-medium">
                        {phases[currentPhase].whatChanges}
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: Tab-based selection with fixed content area */}
        <div className="lg:hidden">
          {/* Tab buttons */}
          <div className="flex overflow-x-auto gap-2 pb-4 mb-6 scrollbar-hide">
            {phases.map((phase, index) => {
              const isActive = activePhase === index;

              return (
                <button
                  key={phase.number}
                  className={`
                    flex-shrink-0 px-4 py-3 rounded-lg border transition-all duration-300
                    ${isActive 
                      ? "bg-primary border-primary text-primary-foreground" 
                      : "bg-background border-border text-muted-foreground"
                    }
                  `}
                  onClick={() => setActivePhase(index)}
                >
                  <span className="text-sm font-semibold whitespace-nowrap">
                    {phase.number}. {phase.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Fixed content container - never collapses */}
          <div className="min-h-[280px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activePhase}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="border border-border rounded-lg p-5 bg-card"
              >
                <h3 className="font-medium text-foreground mb-2">{phases[activePhase].title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{phases[activePhase].description}</p>

                <div className="pt-4 border-t border-border space-y-4">
                  <div>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Decision Made
                    </span>
                    <p className="text-sm text-foreground mt-1">{phases[activePhase].decision}</p>
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Output
                    </span>
                    <p className="text-sm text-foreground mt-1">{phases[activePhase].output}</p>
                  </div>
                  {phases[activePhase].riskRemoved && (
                    <div>
                      <span className="text-xs uppercase tracking-wider text-green-600 font-medium">
                        Risk Removed
                      </span>
                      <p className="text-sm text-foreground mt-1">{phases[activePhase].riskRemoved}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemsTimeline;